import React, { useEffect, useState } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import './TourTypeManagement.css';
import { PriceTierEditor } from './TourTypes/PriceTierEditor';
import { TourTypePriceTier } from '../../types/tourType.types';
import { API_BASE_URL, authFetch, responseErrorText } from '../../config/api.config';
import { formatPrice as formatPriceUtil } from '../../utils/formatters';

const API_BASE = API_BASE_URL;

interface TourType {
  id: number;
  code: string;
  name: string;
  description?: string;
  basePricePerPerson: number;
  minParticipants: number;
  maxParticipants: number;
  sortOrder: number;
  isActive: boolean;
  colorCode?: string;
  iconName?: string;
  pricingMode: string; // "PerPerson" or "FixedPrice"
  fixedPrice?: number;
  createdAt: string;
  updatedAt: string;
  priceTiers?: TourTypePriceTier[];
}

interface TourTypeFormData {
  code: string;
  name: string;
  description: string;
  basePricePerPerson: number;
  minParticipants: number;
  maxParticipants: number;
  sortOrder: number;
  isActive: boolean;
  colorCode: string;
  iconName: string;
  pricingMode: string;
  fixedPrice?: number;
}

const TourTypeManagement: React.FC = () => {
  const [tourTypes, setTourTypes] = useState<TourType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<TourTypeFormData>({
    code: '',
    name: '',
    description: '',
    basePricePerPerson: 65.00,
    minParticipants: 30,
    maxParticipants: 100,
    sortOrder: 0,
    isActive: true,
    colorCode: '#4F46E5',
    iconName: 'star',
    pricingMode: 'PerPerson',
    fixedPrice: undefined
  });
  const [priceTiers, setPriceTiers] = useState<TourTypePriceTier[]>([]);

  useEffect(() => {
    loadTourTypes();
  }, []);

  const loadTourTypes = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await authFetch(`${API_BASE}/tour-types?includeInactive=true`, {});

      if (!response.ok) throw new Error(await responseErrorText(response));

      const data = await response.json();
      setTourTypes(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingId(null);
    setFormData({
      code: '',
      name: '',
      description: '',
      basePricePerPerson: 65.00,
      minParticipants: 30,
      maxParticipants: 100,
      sortOrder: tourTypes.length + 1,
      isActive: true,
      colorCode: '#4F46E5',
      iconName: 'star',
      pricingMode: 'PerPerson',
      fixedPrice: undefined
    });
    setPriceTiers([]);
    setShowDialog(true);
  };

  const handleEdit = (tourType: TourType) => {
    setEditingId(tourType.id);
    setFormData({
      code: tourType.code,
      name: tourType.name,
      description: tourType.description || '',
      basePricePerPerson: tourType.basePricePerPerson,
      minParticipants: tourType.minParticipants,
      maxParticipants: tourType.maxParticipants,
      sortOrder: tourType.sortOrder,
      isActive: tourType.isActive,
      colorCode: tourType.colorCode || '#4F46E5',
      iconName: tourType.iconName || 'star',
      pricingMode: tourType.pricingMode || 'PerPerson',
      fixedPrice: tourType.fixedPrice
    });
    setPriceTiers(tourType.priceTiers || []);
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      const url = editingId
        ? `${API_BASE}/tour-types/${editingId}`
        : `${API_BASE}/tour-types`;

      const method = editingId ? 'PUT' : 'POST';

      // Save TourType first
      // FIX 24.08.2026 (Kundin-Befund 25.08., Floßfahrttyp 2027): authFetch mit
      // automatischem Token-Refresh (401 nach 15 Minuten lieferte vorher nur
      // "Fehler beim Speichern" ohne Grund), Fehlertexte mit Status,
      // Staffelpreis-Requests werden jetzt auf Erfolg geprüft.
      const response = await authFetch(url, {
        method,
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error(await responseErrorText(response));
      }

      const savedTourType = editingId ? { id: editingId } : await response.json();
      const tourTypeId = savedTourType.id;

      // Save price tiers if we're editing or have tiers
      if (priceTiers.length > 0 && tourTypeId) {
        // Delete existing tiers and create new ones
        const existingTiers = await authFetch(`${API_BASE}/tour-types/${tourTypeId}/price-tiers`, {});

        if (existingTiers.ok) {
          const existing = await existingTiers.json();
          // Delete old tiers
          for (const tier of existing) {
            const delResponse = await authFetch(`${API_BASE}/tour-types/${tourTypeId}/price-tiers/${tier.id}`, {
              method: 'DELETE'
            });
            if (!delResponse.ok) {
              throw new Error(`Staffelpreis konnte nicht entfernt werden: ${await responseErrorText(delResponse)}`);
            }
          }
        }

        // Create new tiers
        for (let i = 0; i < priceTiers.length; i++) {
          const tier = priceTiers[i];
          const tierResponse = await authFetch(`${API_BASE}/tour-types/${tourTypeId}/price-tiers`, {
            method: 'POST',
            body: JSON.stringify({
              minParticipants: tier.minParticipants,
              maxParticipants: tier.maxParticipants,
              pricePerPerson: tier.pricePerPerson,
              sortOrder: i
            })
          });
          if (!tierResponse.ok) {
            throw new Error(`Staffelpreis ${tier.minParticipants}-${tier.maxParticipants} konnte nicht gespeichert werden: ${await responseErrorText(tierResponse)}`);
          }
        }
      }

      setShowDialog(false);
      loadTourTypes();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Fehler beim Speichern');
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Tour-Typ "${name}" wirklich löschen?`)) return;

    try {
      const response = await authFetch(`${API_BASE}/tour-types/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(await responseErrorText(response));
      }

      loadTourTypes();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <ArrowPathIcon className="w-12 h-12 animate-spin" />
      </div>
    );
  }

  return (
    <div className="tour-type-management">
      <div className="page-header">
        <h1>Tour-Typen Verwaltung</h1>
        <button className="btn-primary" onClick={handleCreate}>
          <PlusIcon className="icon" />
          Neuer Tour-Typ
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="tour-types-grid">
        {tourTypes.map(tourType => (
          <div key={tourType.id} className="tour-type-card">
            <div className="card-header" style={{ borderColor: tourType.colorCode }}>
              <div className="card-title">
                <span className="code-badge" style={{ backgroundColor: tourType.colorCode }}>
                  {tourType.code}
                </span>
                <h3>{tourType.name}</h3>
              </div>
              <div className="card-actions">
                <button
                  className="btn-icon"
                  onClick={() => handleEdit(tourType)}
                  title="Bearbeiten"
                >
                  <PencilIcon className="icon" />
                </button>
                <button
                  className="btn-icon btn-danger"
                  onClick={() => handleDelete(tourType.id, tourType.name)}
                  title="Löschen"
                >
                  <TrashIcon className="icon" />
                </button>
              </div>
            </div>

            <div className="card-body">
              <p className="description">{tourType.description || 'Keine Beschreibung'}</p>

              <div className="info-grid">
                <div className="info-item">
                  <span className="label">
                    {tourType.pricingMode === 'FixedPrice' ? 'Festpreis:' : 'Preis pro Person:'}
                  </span>
                  <span className="value">
                    {tourType.pricingMode === 'FixedPrice'
                      ? formatPriceUtil(tourType.fixedPrice || tourType.basePricePerPerson)
                      : formatPriceUtil(tourType.basePricePerPerson)}
                  </span>
                </div>
                <div className="info-item">
                  <span className="label">Min. Teilnehmer:</span>
                  <span className="value">{tourType.minParticipants}</span>
                </div>
                <div className="info-item">
                  <span className="label">Max. Teilnehmer:</span>
                  <span className="value">{tourType.maxParticipants}</span>
                </div>
                <div className="info-item">
                  <span className="label">Status:</span>
                  <span className={`badge ${tourType.isActive ? 'badge-success' : 'badge-secondary'}`}>
                    {tourType.isActive ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showDialog && (
        <div className="dialog-overlay" onClick={() => setShowDialog(false)}>
          <div className="dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h2>{editingId ? 'Tour-Typ bearbeiten' : 'Neuer Tour-Typ'}</h2>
              <button className="btn-close" onClick={() => setShowDialog(false)}>×</button>
            </div>

            <div className="dialog-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Code*</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                    disabled={!!editingId}
                    placeholder="z.B. VIP"
                  />
                </div>

                <div className="form-group">
                  <label>Name*</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="z.B. VIP Tour"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Beschreibung</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Beschreibung des Tour-Typs..."
                    rows={3}
                  />
                </div>

                {/* Pricing Mode Selection */}
                <div className="form-group full-width">
                  <label>Preisgestaltung</label>
                  <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        checked={formData.pricingMode === 'PerPerson'}
                        onChange={() => setFormData({...formData, pricingMode: 'PerPerson', fixedPrice: undefined})}
                      />
                      Pro Person (mit Staffelpreisen)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        checked={formData.pricingMode === 'FixedPrice'}
                        onChange={() => setFormData({...formData, pricingMode: 'FixedPrice', fixedPrice: formData.fixedPrice || 12990.00})}
                      />
                      Festpreis
                    </label>
                  </div>
                </div>

                {/* Conditional rendering based on pricing mode */}
                {formData.pricingMode === 'PerPerson' ? (
                  <>
                    <div className="form-group">
                      <label>Basispreis pro Person (€)*</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.basePricePerPerson}
                        onChange={(e) => setFormData({...formData, basePricePerPerson: parseFloat(e.target.value)})}
                      />
                      <small style={{ color: '#6c757d', fontSize: '0.875rem' }}>
                        Wird verwendet wenn keine Staffelpreise definiert sind
                      </small>
                    </div>
                  </>
                ) : (
                  <div className="form-group">
                    <label>Festpreis (brutto, inkl. 19% MwSt.) (€)*</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.fixedPrice || 12990.00}
                      onChange={(e) => setFormData({...formData, fixedPrice: parseFloat(e.target.value)})}
                      required
                    />
                    <small style={{ color: '#6c757d', fontSize: '0.875rem' }}>
                      Preis gilt unabhängig von der Teilnehmerzahl
                    </small>
                  </div>
                )}

                <div className="form-group">
                  <label>Farbe</label>
                  <input
                    type="color"
                    value={formData.colorCode}
                    onChange={(e) => setFormData({...formData, colorCode: e.target.value})}
                  />
                </div>

                {/* Price Tier Editor - Only for PerPerson mode */}
                {formData.pricingMode === 'PerPerson' && (
                  <div className="form-group full-width" style={{ gridColumn: '1 / -1' }}>
                    <PriceTierEditor
                      priceTiers={priceTiers}
                      onChange={setPriceTiers}
                      basePricePerPerson={formData.basePricePerPerson}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Min. Teilnehmer*</label>
                  <input
                    type="number"
                    value={formData.minParticipants}
                    onChange={(e) => setFormData({...formData, minParticipants: parseInt(e.target.value)})}
                  />
                </div>

                <div className="form-group">
                  <label>Max. Teilnehmer*</label>
                  <input
                    type="number"
                    value={formData.maxParticipants}
                    onChange={(e) => setFormData({...formData, maxParticipants: parseInt(e.target.value)})}
                  />
                </div>

                <div className="form-group">
                  <label>Sortierung</label>
                  <input
                    type="number"
                    value={formData.sortOrder}
                    onChange={(e) => setFormData({...formData, sortOrder: parseInt(e.target.value)})}
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                    />
                    Aktiv
                  </label>
                </div>
              </div>
            </div>

            <div className="dialog-footer">
              <button className="btn-secondary" onClick={() => setShowDialog(false)}>
                Abbrechen
              </button>
              <button className="btn-primary" onClick={handleSave}>
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TourTypeManagement;
